// Detailed, photo-matched building models. Each builder works in a local frame:
//   +x = towards the wide end of the footprint (glass front / tower octagon),
//   +z = lateral, +y = up. The frame (from build_data.py) places it on the surveyed footprint.
import * as THREE from 'three/webgpu';
import { uniform, instancedBufferAttribute, vec3, fract, sin, cos } from 'three/tsl';
import { glowScaled } from './materials.js';

const C225 = Math.cos(Math.PI / 8);

/** Collects geometry per material, then merges it into one mesh per material. */
export class Kit {
  constructor() { this.parts = new Map(); }
  add(mat, geo) {
    if (!this.parts.has(mat)) this.parts.set(mat, []);
    this.parts.get(mat).push(geo.index ? geo.toNonIndexed() : geo);
    this.last = mat;
    return this;
  }
  /** WS-37: room behind the last added window part (facade-UV materials): floor and ceiling heights, local y.
   *  `plain` = nothing is known about the inside: a plain glow instead of the generic room. */
  room(floor, ceil, plain = false) { const l = this.parts.get(this.last); if (l) l[l.length - 1].userData.room = { floor, ceil, plain }; return this; }
  box(mat, x, y, z, sx, sy, sz, ry = 0, rx = 0, rz = 0) {
    const g = new THREE.BoxGeometry(sx, sy, sz);
    if (rz) g.rotateZ(rz);
    if (rx) g.rotateX(rx);
    if (ry) g.rotateY(ry);
    g.translate(x, y, z);
    return this.add(mat, g);
  }
  cyl(mat, x, y, z, rTop, rBot, h, seg = 10, rx = 0, rz = 0, ry = 0) {
    const g = new THREE.CylinderGeometry(rTop, rBot, h, seg);
    if (rz) g.rotateZ(rz);
    if (rx) g.rotateX(rx);
    if (ry) g.rotateY(ry);
    g.translate(x, y, z);
    return this.add(mat, g);
  }
  /** A rod between two points. */
  rod(mat, a, b, r = 0.04, seg = 5) {
    const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b);
    const len = A.distanceTo(B);
    const g = new THREE.CylinderGeometry(r, r, len, seg);
    g.translate(0, len / 2, 0);
    g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), B.clone().sub(A).normalize()));
    g.translate(A.x, A.y, A.z);
    return this.add(mat, g);
  }
  tris(mat, list) {
    const p = [];
    for (const [a, b, c] of list) p.push(...a, ...b, ...c);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
    g.computeVertexNormals();
    return this.add(mat, g);
  }
  quad(mat, a, b, c, d) { return this.tris(mat, [[a, b, c], [a, c, d]]); }
  /** A plate whose top face is the quad abcd, `t` thick, extruded down along the quad normal. */
  slab(mat, a, b, c, d, t) {
    const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), C = new THREE.Vector3(...c);
    const n = B.clone().sub(A).cross(C.clone().sub(A)).normalize();
    if (n.y < 0) n.negate();
    const off = (p) => [p[0] - n.x * t, p[1] - n.y * t, p[2] - n.z * t];
    const [a2, b2, c2, d2] = [a, b, c, d].map(off);
    return this.tris(mat, [
      [a, b, c], [a, c, d], [a2, c2, b2], [a2, d2, c2],
      [a, a2, b2], [a, b2, b], [b, b2, c2], [b, c2, c], [c, c2, d2], [c, d2, d], [d, d2, a2], [d, a2, a],
    ]);
  }
  /** Vertical prism from a plan polygon [[x, z], ...] between y0 and y0 + h (optionally hollow). */
  prism(mat, plan, y0, h, hole = null) {
    const shape = new THREE.Shape(plan.map(([x, z]) => new THREE.Vector2(x, -z)));
    if (hole) shape.holes.push(new THREE.Path(hole.map(([x, z]) => new THREE.Vector2(x, -z))));
    const g = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false });
    g.rotateX(-Math.PI / 2);
    g.translate(0, y0, 0);
    return this.add(mat, g);
  }
  /** Wall ring along a convex plan: outer plan, `t` thick. */
  ring(mat, plan, y0, h, t = 0.14) { return this.prism(mat, plan, y0, h, insetConvex(plan, t)); }
  /** Gable-end wall in the plane x = x0, profile [[z, y], ...], `t` thick towards -x. */
  gable(mat, x0, profile, t = 0.14) {
    const shape = new THREE.Shape(profile.map(([z, y]) => new THREE.Vector2(-z, y)));
    const g = new THREE.ExtrudeGeometry(shape, { depth: t, bevelEnabled: false });
    g.rotateY(Math.PI / 2);
    g.translate(x0 - t, 0, 0);
    return this.add(mat, g);
  }
  build() {
    const group = new THREE.Group();
    for (const [mat, list] of this.parts) {
      for (const g of list) { g.deleteAttribute('uv'); if (!g.attributes.normal) g.computeVertexNormals(); if (mat.userData.facadeUV) facadeUV(g); else boxUV(g); }
      const merged = mergeList(list);
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = !mat.transparent;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    return group;
  }
}

function mergeList(list) {
  let count = 0;
  for (const g of list) count += g.attributes.position.count;
  const pos = new Float32Array(count * 3), nor = new Float32Array(count * 3), uv = new Float32Array(count * 2);
  const glow = list.every((g) => g.attributes.glow) ? new Float32Array(count) : null; // per-vertex night glow mask (materials.js glowMaskNode)
  let o = 0;
  for (const g of list) {
    pos.set(g.attributes.position.array, o * 3);
    nor.set(g.attributes.normal.array, o * 3);
    uv.set(g.attributes.uv.array, o * 2);
    if (glow) glow.set(g.attributes.glow.array, o);
    o += g.attributes.position.count;
  }
  const m = new THREE.BufferGeometry();
  m.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  m.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  m.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  if (glow) m.setAttribute('glow', new THREE.BufferAttribute(glow, 1));
  return m;
}

/** Box-projected UVs in metres/2, so board and snow textures keep a real-world scale. */
function boxUV(g) {
  const p = g.attributes.position, n = g.attributes.normal;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u, v;
    if (ay > ax && ay > az) { u = p.getX(i); v = p.getZ(i); }
    else if (ax > az) { u = p.getZ(i); v = p.getY(i); }
    else { u = p.getX(i); v = p.getY(i); }
    uv[i * 2] = u * 0.5; uv[i * 2 + 1] = v * 0.5;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** WS-37: facade UVs for interior-mapped windows: u = metres along the wall (direction cross(up, n), the same in
 *  the shader after any y rotation), v = height in the room scaled so floor..ceiling = 0..3. Horizontal faces: 0. */
function facadeUV(g) {
  const p = g.attributes.position, n = g.attributes.normal, { floor = 0.2, ceil = 3.2, plain = false } = g.userData.room || {};
  const k = 3 / Math.max(0.5, ceil - floor), uv = new Float32Array(p.count * 2), lift = plain ? 100 : 0;
  for (let i = 0; i < p.count; i++) {
    const nx = n.getX(i), nz = n.getZ(i), hl = Math.hypot(nx, nz);
    if (hl < 0.3) continue;
    uv[i * 2] = (p.getX(i) * nz - p.getZ(i) * nx) / hl;
    uv[i * 2 + 1] = (p.getY(i) - floor) * k + lift;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

export function insetConvex(plan, d) {
  const n = plan.length;
  let area = 0;
  for (let i = 0; i < n; i++) area += plan[i][0] * plan[(i + 1) % n][1] - plan[(i + 1) % n][0] * plan[i][1];
  const s = area > 0 ? 1 : -1;
  const lines = plan.map((a, i) => {
    const b = plan[(i + 1) % n];
    const dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz);
    const nx = (-dz / len) * s, nz = (dx / len) * s; // inward normal
    return { p: [a[0] + nx * d, a[1] + nz * d], d: [dx / len, dz / len] };
  });
  return lines.map((l1, i) => {
    const l0 = lines[(i - 1 + n) % n];
    const den = l0.d[0] * l1.d[1] - l0.d[1] * l1.d[0];
    if (Math.abs(den) < 1e-6) return l1.p;
    const t = ((l1.p[0] - l0.p[0]) * l1.d[1] - (l1.p[1] - l0.p[1]) * l1.d[0]) / den;
    return [l0.p[0] + l0.d[0] * t, l0.p[1] + l0.d[1] * t];
  });
}

const octagon = (cx, cz, apothem) => Array.from({ length: 8 }, (_, k) => {
  const a = Math.PI / 8 + (k * Math.PI) / 4, R = apothem / C225;
  return [cx + Math.cos(a) * R, cz + Math.sin(a) * R];
});

/** Gable roof over x in [x0, x1], half-width a (walls), eave height y, ridge height H. */
function gableRoof(kit, M, x0, x1, a, y, H, { overhang = 0.38, snow = 0.32, ridgeSnow = true, skip = null } = {}) {
  const k = (H - y) / a;
  const e = overhang, ye = y - e * k;
  for (const s of [1, -1]) {
    if (skip === s) continue;
    const quad = [[x0, H, 0], [x1, H, 0], [x1, ye, s * (a + e)], [x0, ye, s * (a + e)]];
    kit.slab(M.roofEdge, ...quad, 0.14);
    const up = (p, d) => [p[0], p[1] + d, p[2]];
    kit.slab(M.snow, ...quad.map((p) => up(p, snow)), snow - 0.02);
  }
  if (ridgeSnow) kit.cyl(M.snow, (x0 + x1) / 2, H + snow * 0.75, 0, 0.26, 0.26, x1 - x0, 8, 0, Math.PI / 2);
}

function door(kit, M, x, z, y0, w = 0.95, h = 2.0, faceX = true, mat = M.pine) {
  if (faceX) kit.box(mat, x, y0 + h / 2, z, 0.08, h, w);
  else kit.box(mat, x, y0 + h / 2, z, w, h, 0.08);
}

// ---------------------------------------------------------------------------------------------
/** Aurora Cabin / Aurora Suite / Panorama Sauna: low walls, gable rear, glass half-pyramid front. */
// WS-10: optional params, all off by default (same look as before): ridgeH = ridge/glass apex height above
// base (photo-measured); frontEave = width (m) of the dark sloped eave skirt between the front walls and the
// glass; chimneyAt = [x, z] of the stove pipe in the unit frame (pipe top 0.75 m above the roof there).
export function glassFront(f, M, { wallH: wallHp = 1.3, porch = 0.9, roofWindow = null, chimney = false, sauna = false, ridgeH = null, frontEave = 0, chimneyAt = null, rearW = 0, ...more } = {}) {
  if (rearW > 0) return glassOctagon(f, M, { wallH: wallHp, porch, chimney, ridgeH, frontEave, chimneyAt, rearW, ...more }); // WS-10: octagon + narrow rear gable block
  const kit = new Kit();
  const L = f.L, a = Math.max(2.3, (f.W - 0.25) / 2);
  const porchD = porch;
  const xr = -L / 2 + porchD, xg = L / 2 - a;
  const plinth = 0.32, wallH = wallHp, H = ridgeH || wallH + a * 1.02; // WS-10: ridgeH
  const t45 = Math.tan(Math.PI / 8) * a;
  const plan = [[xr, -a], [xg, -a], [xg + t45, -a], [L / 2, -t45], [L / 2, t45], [xg + t45, a], [xg, a], [xr, a]];
  kit.prism(M.plinth, insetConvex(plan, -0.02), 0, plinth);
  kit.ring(M.wood, plan, plinth, wallH - plinth, 0.16);
  kit.ring(M.trim, insetConvex(plan, -0.05), wallH, 0.12, 0.2);
  for (const [x, z] of plan) kit.box(M.post, x, (wallH + plinth) / 2, z, 0.16, wallH - plinth, 0.16);

  // Rear gable wall with door, lamp and entrance deck.
  const profile = [[-a, 0], [a, 0], [a, wallH], [0, H], [-a, wallH]];
  kit.gable(M.wood, xr + 0.15, profile, 0.15);
  door(kit, M, xr - 0.02, a * 0.35, plinth + 0.05);
  kit.box(M.lamp, xr - 0.06, plinth + 2.25, a * 0.35, 0.08, 0.14, 0.14);
  kit.box(M.deck, -L / 2 + porchD / 2 - 0.05, 0.22, a * 0.25, porchD + 0.1, 0.12, Math.min(f.narrow + 0.4, 2 * a));
  kit.box(M.deck, -L / 2 - 0.25, 0.1, a * 0.25, 0.45, 0.08, 1.4);

  // Snow-covered gable roof over the rear part, with a stove pipe.
  gableRoof(kit, M, xr - 0.45, xg, a, wallH, H);
  const smoke = [];
  if (chimney && chimneyAt) { // WS-10: photo-placed stove pipe, top 0.75 m above the roof at that point
    const [cx, cz] = chimneyAt, roofY = H - (Math.abs(cz) * (H - wallH)) / a;
    kit.cyl(M.metal, cx, roofY - 0.05, cz, 0.09, 0.09, 1.6, 8);
    smoke.push([cx, roofY + 0.85, cz]);
  } else if (chimney) {
    kit.cyl(M.metal, xr + 0.9, H + 0.1, -a * 0.45, 0.09, 0.09, 1.6, 8);
    smoke.push([xr + 0.9, H + 1.0, -a * 0.45]);
  }

  // Glass half-pyramid: five panes from the front wall top to the ridge end, with dark mullions.
  const yG = wallH + 0.12, apex = [xg, H, 0];
  let front = [[xg, a], [xg + t45, a], [L / 2, t45], [L / 2, -t45], [xg + t45, -a], [xg, -a]].map(([x, z]) => [x, yG, z]);
  if (frontEave > 0) { // WS-10: dark sloped eave skirt around the front; the glass starts at its inner edge
    const e = frontEave, inner = front.map(([x, y, z]) => { const dx = xg - x, dz = -z, d = Math.hypot(dx, dz) || 1; return [x + (dx / d) * e, y + e * 0.55, z + (dz / d) * e]; });
    const outer = front.map(([x, y, z]) => { const dx = x - xg, d = Math.hypot(dx, z) || 1; return [x + (dx / d) * 0.12, y - 0.06, z + (z / d) * 0.12]; });
    for (let i = 0; i < front.length - 1; i++) kit.slab(M.roofEdge, outer[i], outer[i + 1], inner[i + 1], inner[i], 0.08);
    front = inner;
  }
  const panes = [];
  for (let i = 0; i < front.length - 1; i++) panes.push([front[i], front[i + 1], apex]);
  kit.tris(M.glass, panes);
  for (const p of front) kit.rod(M.metal, p, apex, 0.035);
  for (let i = 0; i < front.length - 1; i++) kit.rod(M.metal, front[i], front[i + 1], 0.045);

  // Interior visible through the glass: warm floor, bed, partition wall.
  const inner = insetConvex(plan, 0.2);
  kit.prism(M.interior, inner, plinth, 0.08);
  kit.gable(M.interior, xg - 1.7, [[-a + 0.2, plinth], [a - 0.2, plinth], [a - 0.2, wallH], [0, H - 0.25], [-a + 0.2, wallH]], 0.1);
  if (sauna) {
    kit.box(M.pine, xg + 0.2, plinth + 0.45, 0, 1.2, 0.5, 2 * a - 0.6); // benches
    kit.box(M.pine, xg - 0.6, plinth + 0.9, 0, 0.9, 0.5, 2 * a - 0.6);
  } else {
    kit.box(M.linen, xg - 0.4, plinth + 0.35, 0, 2.1, 0.45, 1.9);
    kit.box(M.wood, xg - 1.5, plinth + 0.75, 0, 0.12, 1.0, 2.0);
    kit.box(M.lamp, xg - 1.35, plinth + 0.8, a - 0.55, 0.18, 0.25, 0.18);
  }
  if (roofWindow) {
    // One raised roof window on the slope to the RIGHT of someone facing the glass front (local -z),
    // or the left (+z). Local +x is the glass end, so a viewer facing it looks along -x.
    const s = roofWindow === 'right' ? -1 : 1;
    const k = (H - wallH) / a;
    const zA = s * 0.45, zB = s * Math.min(a - 0.6, 1.9);
    const yA = H - 0.45 * k + 0.36, yB = H - Math.abs(zB) * k + 0.36;
    const x0 = xr + 0.9, x1 = Math.min(xg - 0.3, xr + 3.3);
    kit.slab(M.roofEdge, [x0 - 0.12, yA + 0.12, zA], [x1 + 0.12, yA + 0.12, zA], [x1 + 0.12, yB + 0.12, zB], [x0 - 0.12, yB + 0.12, zB], 0.35);
    kit.quad(M.skylight, [x0, yA + 0.14, zA], [x1, yA + 0.14, zA], [x1, yB + 0.14, zB], [x0, yB + 0.14, zB]);
  }
  return { group: kit.build(), top: H + 0.4, smoke };
}

// ---------------------------------------------------------------------------------------------
/** Aurora Tower / Tower Suite: octagon with tall truncated pyramid roof and apex skylight,
 *  gabled entrance annex on the narrow end; suites add a glass-gabled bedroom wing. */
export function tower(f, M, { annexDepth = 2.3, wing = false, wingSide = 1, roofWindow = wing } = {}) {
  const suite = wing;
  const kit = new Kit();
  const L = f.L, r = Math.max(2.6, f.W / 2), ocx = L / 2 - r;
  const plinth = 0.32, wallH = 2.7, roofH = 3.5;
  const oct = octagon(ocx, 0, r);
  kit.prism(M.plinth, insetConvex(oct, -0.02), 0, plinth);
  kit.ring(M.wood, oct, plinth, wallH - plinth);
  kit.ring(M.trim, insetConvex(oct, -0.05), wallH, 0.12, 0.2);
  for (const [x, z] of oct) kit.box(M.post, x, (wallH + plinth) / 2, z, 0.17, wallH - plinth, 0.17);

  // Truncated octagonal pyramid roof, snow-covered, with an octagonal glass skylight.
  const eave = octagon(ocx, 0, r + 0.4), top = octagon(ocx, 0, r * 0.36);
  const yE = wallH - 0.25, yT = wallH + roofH;
  const glassFace = roofWindow ? 2 : -1; // tower suites: one roof face is a glass bedroom window
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    const q = [[eave[i][0], yE, eave[i][1]], [eave[j][0], yE, eave[j][1]], [top[j][0], yT, top[j][1]], [top[i][0], yT, top[i][1]]];
    kit.slab(M.roofEdge, ...q, 0.14);
    kit.slab(M.snow, ...q.map((p) => [p[0], p[1] + 0.3, p[2]]), 0.28);
    if (i === glassFace) {
      const lerp = (p, c, t) => p.map((v, n) => v + (c[n] - v) * t);
      const cc = [(q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4 + 0.36, (q[0][2] + q[1][2] + q[2][2] + q[3][2]) / 4];
      const g = q.map((p) => lerp([p[0], p[1] + 0.34, p[2]], cc, 0.32));
      kit.quad(M.skylight, ...g);
    }
  }
  const collar = top.map(([x, z]) => [x, z]);
  kit.ring(M.trim, insetConvex(collar, -0.08), yT + 0.28, 0.42, 0.22);
  const cap = insetConvex(collar, 0.05);
  const apex = [ocx, yT + 0.95, 0];
  kit.tris(M.glass, cap.map((p, i) => [[p[0], yT + 0.7, p[1]], [cap[(i + 1) % 8][0], yT + 0.7, cap[(i + 1) % 8][1]], apex]));
  kit.prism(M.interior, cap, yT + 0.1, 0.06);
  kit.prism(M.interior, insetConvex(oct, 0.2), plinth, 0.08);

  // Entrance annex: gabled box out to the narrow end, shingled gable with a warm LED edge.
  // Annex depth comes from the photos; the survey footprint is longer than the built annex.
  const aw = Math.max(1.5, (f.narrow + 0.35) / 2), x0 = ocx - r - annexDepth, x1 = ocx;
  const aH = 2.45, aR = aH + aw * 1.0;
  const annex = [[x0, -aw], [x1, -aw], [x1, aw], [x0, aw]];
  kit.prism(M.plinth, annex, 0, plinth);
  kit.ring(M.wood, annex, plinth, aH - plinth);
  kit.gable(M.wood, x0 + 0.15, [[-aw, 0], [aw, 0], [aw, aH], [-aw, aH]], 0.15);
  kit.gable(M.shingle, x0 + 0.05, [[-aw, aH], [aw, aH], [0, aR]], 0.12);
  gableRoof(kit, M, x0 - 0.35, x1, aw, aH, aR, { overhang: 0.3, snow: 0.26 });
  const ledA = [x0 - 0.12, aH + 0.02, -aw - 0.1], ledB = [x0 - 0.12, aR + 0.05, 0], ledC = [x0 - 0.12, aH + 0.02, aw + 0.1];
  kit.rod(M.led, ledA, ledB, 0.03); kit.rod(M.led, ledB, ledC, 0.03);
  door(kit, M, x0 - 0.03, 0, plinth + 0.05, 1.0, 2.0);
  kit.box(M.trim, x0 - 0.05, plinth + 2.25, 0, 0.04, 0.22, 0.45); // room number plate
  kit.box(M.deck, x0 - 0.7, 0.2, 0, 1.3, 0.12, 2 * aw);
  kit.box(M.deck, x0 - 1.45, 0.08, 0, 0.4, 0.08, 1.6);

  if (suite) {
    // Bedroom wing on the lateral side with a big glass gable (the extra glass-roof bedroom).
    const ww = 1.8, wl = r + 3.4, wH = 2.45, wR = wH + ww * 0.95, s = wingSide;
    const zEnd = s * wl;
    const wing = [[ocx - ww, 0], [ocx + ww, 0], [ocx + ww, zEnd], [ocx - ww, zEnd]];
    kit.prism(M.plinth, wing, 0, plinth);
    kit.ring(M.wood, wing, plinth, wH - plinth);
    // Roof along z: build in a rotated sub-kit.
    const sub = new Kit();
    gableRoof(sub, M, 0, wl + 0.35, ww, wH, wR, { overhang: 0.3, snow: 0.26 });
    for (const [mat, list] of sub.parts) for (const g of list) { g.rotateY(-s * Math.PI / 2); g.translate(ocx, 0, 0); kit.add(mat, g); }
    // Glass gable end with mullions and a lit bedroom behind.
    const ge = zEnd + s * 0.02;
    const gpts = [[ocx - ww, plinth + 0.25, ge], [ocx + ww, plinth + 0.25, ge], [ocx + ww, wH, ge], [ocx, wR, ge], [ocx - ww, wH, ge]];
    kit.tris(M.glass, [[gpts[0], gpts[1], gpts[2]], [gpts[0], gpts[2], gpts[4]], [gpts[4], gpts[2], gpts[3]]]);
    kit.rod(M.metal, [ocx, plinth + 0.25, ge], [ocx, wR, ge], 0.04);
    kit.rod(M.metal, [ocx - ww, wH, ge], [ocx + ww, wH, ge], 0.04);
    kit.box(M.linen, ocx, plinth + 0.35, zEnd - s * 1.4, 1.9, 0.45, 2.0);
    kit.box(M.interior, ocx, plinth + 0.04, zEnd - s * 1.6, 2 * ww - 0.3, 0.08, 3.0);
    kit.box(M.interior, ocx, (wR + plinth) / 2, zEnd - s * 2.9, 2 * ww - 0.3, wR - plinth - 0.3, 0.1);
  }
  return { group: kit.build(), top: yT + 1.4, smoke: [] };
}

// ---------------------------------------------------------------------------------------------
// VIS-222: the model of a footprint that resort.json names nowhere (no unit, no buildings.<key>): a plain hip block
// for a footprint with an inset (complex outline), else a cottage (lower for a shed, MML class 42261), drawn neutral
// (the owner's pick "Plain look"): no door, windows or lamp, since nothing shows where they are (rules.md "generic
// fallbacks draw no openings"). A footprint that needs them gets a units/buildings entry with its source.
export function fallbackModel(b) {
  if (b.inset) return { model: 'hip', params: { wallH: 3, roofH: 2.4 } };
  return { model: 'cottage', params: { wallH: b.cls === 42261 ? 2.3 : 2.7, door: false, windows: false, light: false } };
}

// ---------------------------------------------------------------------------------------------
/** Small gabled cottage from the footprint's oriented box (gift shop, staff houses, sheds). */
// WS-13: optional params, defaults = the previous look: wall = material key of the walls (e.g. 'pine' light
// tan boards, 'trim' white panels); roof = 'flat' (snow-covered flat roof, no gables); door = false drops the
// door, its trim, step and lamp (neutral when the entrance side has no reference).
export function cottage(f, M, { wallH = 2.6, pitch = 0.85, windows = true, sign = false, whiteDoor = false, light = true, wall = 'wood', roof = 'gable', door: hasDoor = true } = {}) {
  if (roof === 'flat' || wall !== 'wood' || !hasDoor) return cottageVariant(f, M, { wallH, pitch, windows, light, wall, roof, hasDoor }); // WS-13
  const kit = new Kit();
  const L = f.L, a = f.W / 2, plinth = 0.4, H = wallH + a * pitch;
  const plan = [[-L / 2, -a], [L / 2, -a], [L / 2, a], [-L / 2, a]];
  kit.prism(M.plinth, insetConvex(plan, -0.02), 0, plinth);
  kit.ring(M.wood, plan, plinth, wallH - plinth);
  for (const x of [-L / 2, L / 2]) kit.gable(M.wood, x + (x > 0 ? 0 : 0.15), [[-a, wallH - 0.01], [a, wallH - 0.01], [0, H]], 0.15);
  for (const [x, z] of plan) kit.box(M.trim, x, wallH / 2 + 0.2, z, 0.14, wallH - plinth, 0.14);
  gableRoof(kit, M, -L / 2 - 0.4, L / 2 + 0.4, a, wallH, H, { overhang: 0.45 });
  kit.box(M.trim, -L / 2 - 0.42, H - 0.2, 0, 0.06, 0.15, 0.15);
  // Door on the long side, windows with white trims either side.
  const dz = a + 0.02;
  door(kit, M, 0, dz, plinth + 0.05, 0.95, 2.0, false, whiteDoor ? M.trim : M.pine);
  kit.box(M.trim, 0, plinth + 2.15, dz, 1.25, 0.14, 0.06);
  kit.box(M.deck, 0, 0.25, dz + 0.7, 1.8, 0.14, 1.3);
  if (windows) {
    for (const x of [-L * 0.3, L * 0.3]) {
      kit.box(M.windowWarm, x, plinth + 1.35, dz, 0.9, 0.95, 0.05);
      kit.box(M.pine, x, plinth + 1.35, dz + 0.02, 1.08, 1.12, 0.03);
      kit.box(M.windowWarm, x, plinth + 1.35, dz + 0.04, 0.9, 0.95, 0.03);
    }
  }
  if (light) kit.box(M.lamp, 0.85, plinth + 2.0, dz + 0.1, 0.16, 0.24, 0.16);
  if (sign) kit.cyl(M.trim, -L / 2 + 1.2, plinth + 2.0, dz + 0.08, 0.42, 0.42, 0.05, 20, Math.PI / 2);
  return { group: kit.build(), top: H + 0.5, smoke: [] };
}

// ---- WS-13 (begin): cottage variants (wall material, flat roof, no door) and the lavvu prop ----------------
function cottageVariant(f, M, { wallH, pitch, windows, light, wall, roof, hasDoor }) {
  const kit = new Kit();
  const W = M[wall] || M.wood, L = f.L, a = f.W / 2, plinth = 0.35;
  const flat = roof === 'flat', H = flat ? wallH : wallH + a * pitch;
  const plan = [[-L / 2, -a], [L / 2, -a], [L / 2, a], [-L / 2, a]];
  kit.prism(M.plinth, insetConvex(plan, -0.02), 0, plinth);
  kit.ring(W, plan, plinth, wallH - plinth);
  if (flat) {
    kit.prism(M.roofEdge, insetConvex(plan, -0.12), wallH, 0.14); // thin roof slab with a small overhang
    kit.prism(M.snow, insetConvex(plan, 0.02), wallH + 0.14, 0.32); // snow cap (winter photos show snow on the roofs)
  } else {
    for (const x of [-L / 2, L / 2]) kit.gable(W, x + (x > 0 ? 0 : 0.15), [[-a, wallH - 0.01], [a, wallH - 0.01], [0, H]], 0.15);
    gableRoof(kit, M, -L / 2 - 0.4, L / 2 + 0.4, a, wallH, H, { overhang: 0.45 });
  }
  for (const [x, z] of plan) kit.box(M.trim === W ? M.plinth : M.trim, x, wallH / 2 + 0.2, z, 0.14, wallH - plinth, 0.14);
  const dz = a + 0.02;
  if (hasDoor) {
    door(kit, M, 0, dz, plinth + 0.05, 0.95, 2.0, false, M.pine);
    kit.box(M.deck, 0, 0.25, dz + 0.7, 1.8, 0.14, 1.3);
    if (light) kit.box(M.lamp, 0.85, plinth + 2.0, dz + 0.1, 0.16, 0.24, 0.16);
  }
  if (windows) for (const x of [-L * 0.3, L * 0.3]) {
    kit.box(M.windowWarm, x, plinth + 1.35, dz, 0.9, 0.95, 0.05);
    kit.box(M.trim, x, plinth + 1.35, dz + 0.02, 1.06, 1.1, 0.03);
    kit.box(M.windowWarm, x, plinth + 1.35, dz + 0.04, 0.9, 0.95, 0.03);
  }
  return { group: kit.build(), top: H + 0.5, smoke: [] };
}

/** Sami lavvu (teepee): conical canvas on crossed poles. Params: r base radius, h canvas height, color (canvas
 *  hex, from the photo), poles = count, porch = { l, w, wallH, door } wooden entrance on +x (only with a photo).
 *  No door, fire or smoke unless a reference shows them. */
export function lavvu(M, { r = 2.3, h = 4.2, color = null, poles = 8, porch = null } = {}) {
  const kit = new Kit();
  M._canvasTint = M._canvasTint || new Map();
  if (color && !M._canvasTint.has(color)) M._canvasTint.set(color, new THREE.MeshStandardNodeMaterial({ color, roughness: 0.95, side: THREE.DoubleSide }));
  const cloth = color ? M._canvasTint.get(color) : M.canvas;
  kit.add(cloth, new THREE.ConeGeometry(r, h, 14, 1, true).translate(0, h / 2, 0));
  kit.add(M.snow, new THREE.ConeGeometry(r + 0.12, 0.35, 14, 1, true).translate(0, 0.17, 0)); // snow drift around the hem
  for (let i = 0; i < poles; i++) { // poles cross at the smoke hole and stick out above it
    const t = (i / poles) * Math.PI * 2, c = Math.cos(t), s = Math.sin(t);
    kit.rod(M.pine, [c * r * 0.98, 0, s * r * 0.98], [-c * 0.35, h + 0.75, -s * 0.35], 0.045);
  }
  if (porch) { // round 4b: wooden gabled entrance vestibule on the +x side (photo), door with a small window
    const { l = 1.6, w = 1.5, wallH = 1.9, door = true } = porch === true ? {} : porch;
    const x0 = r * 0.6, x1 = r * 0.6 + l, a = w / 2, H = wallH + a * 0.9;
    kit.ring(M.pine, [[x0, -a], [x1, -a], [x1, a], [x0, a]], 0, wallH, 0.08);
    kit.gable(M.pine, x1, [[-a, wallH - 0.01], [a, wallH - 0.01], [0, H]], 0.08);
    gableRoof(kit, M, x0 - 0.1, x1 + 0.25, a, wallH, H, { overhang: 0.2 });
    if (door) {
      kit.box(M.wood, x1 + 0.01, 0.95, 0, 0.05, 1.85, 0.8);
      kit.box(M.windowWarm, x1 + 0.04, 1.45, 0, 0.02, 0.35, 0.45);
    }
  }
  return { group: kit.build(), top: h + 1, smoke: [] };
}
// ---- WS-13 (end) ---------------------------------------------------------------------------------------

/** Octagonal kota-style hall: big warm windows, steep pyramid roof with chimney cap. */
export function octagonHall(f, M) {
  const kit = new Kit();
  const r = Math.max(3.5, Math.min(f.L, f.W) / 2 - 0.2), wallH = 2.8, plinth = 0.3, roofH = 4.8;
  const oct = octagon(0, 0, r);
  kit.prism(M.plinth, insetConvex(oct, -0.02), 0, plinth);
  for (let i = 0; i < 8; i++) {
    const a = oct[i], b = oct[(i + 1) % 8];
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]), ang = -Math.atan2(b[1] - a[1], b[0] - a[0]);
    if (i % 4 === 0) kit.box(M.wood, mid[0], (wallH + plinth) / 2, mid[1], len, wallH - plinth, 0.18, ang);
    else {
      kit.box(M.windowWarm, mid[0], (wallH + plinth) / 2 + 0.1, mid[1], len - 0.3, wallH - plinth - 0.6, 0.06, ang);
      kit.box(M.wood, mid[0], plinth + 0.25, mid[1], len, 0.5, 0.18, ang);
      kit.box(M.wood, mid[0], wallH - 0.15, mid[1], len, 0.3, 0.18, ang);
    }
    kit.box(M.pine, a[0], (wallH + plinth) / 2, a[1], 0.22, wallH - plinth, 0.22);
  }
  const eave = octagon(0, 0, r + 0.6), top = octagon(0, 0, 0.45);
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    const q = [[eave[i][0], wallH - 0.2, eave[i][1]], [eave[j][0], wallH - 0.2, eave[j][1]], [top[j][0], wallH + roofH, top[j][1]], [top[i][0], wallH + roofH, top[i][1]]];
    kit.slab(M.roofEdge, ...q, 0.16);
    kit.slab(M.snow, ...q.map((p) => [p[0], p[1] + 0.32, p[2]]), 0.3);
  }
  kit.cyl(M.metal, 0, wallH + roofH + 0.6, 0, 0.3, 0.38, 1.0, 12);
  kit.cyl(M.metal, 0, wallH + roofH + 1.2, 0, 0.05, 0.55, 0.35, 12);
  return { group: kit.build(), top: wallH + roofH + 1.6, smoke: [[0, wallH + roofH + 1.5, 0]] };
}

/** Canvas yurt (Jurtta) with a deck and string lights. */
export function yurt(f, M, { deckSide = 1 } = {}) {
  const kit = new Kit();
  const r = Math.max(3.5, Math.sqrt(f.L * f.W) / 2 - 0.3), wallH = 2.1;
  kit.cyl(M.deck, 0, 0.2, 0, r + 0.4, r + 0.4, 0.3, 24);
  kit.cyl(M.canvas, 0, 0.35 + wallH / 2, 0, r, r, wallH, 32);
  kit.cyl(M.canvas, 0, 0.35 + wallH + 0.9, 0, 0.9, r + 0.25, 1.8, 32);
  kit.cyl(M.snow, 0, 0.35 + wallH + 0.95, 0, 0.95, r + 0.3, 1.7, 32);
  kit.cyl(M.trim, 0, 0.35 + wallH + 1.9, 0, 0.6, 0.95, 0.25, 16);
  kit.cyl(M.metal, r * 0.55, 0.35 + wallH + 1.6, 0, 0.12, 0.12, 2.2, 8);
  const dx = deckSide * (r + 1.6);
  kit.box(M.deck, dx * 0.9, 0.3, 0, 3.2, 0.2, 4.6);
  kit.box(M.pine, deckSide * (r + 0.03), 0.4 + 1.0, 0, 0.1, 2.0, 1.8);
  for (let i = 0; i < 9; i++) {
    const a = -0.9 + i * 0.22;
    kit.box(M.lamp, deckSide * Math.cos(a) * (r + 0.05), 0.35 + wallH - 0.05 - Math.abs(Math.sin(i * 0.7)) * 0.15, Math.sin(a) * (r + 0.05), 0.08, 0.08, 0.08);
  }
  return { group: kit.build(), top: 0.35 + wallH + 2.6, smoke: [[r * 0.55, 0.35 + wallH + 2.8, 0]] };
}

/** Wood-fired barrel sauna on a deck, with stairs down to an ice hole. */
export function barrelSauna(M, p = {}, groundY = () => 0) {
  if (p.terrace) return barrelOnTerrace(M, p, groundY); // WS-10: photo-matched deck, stairs, ice hole (params)
  const kit = new Kit();
  kit.box(M.deck, 0, 1.0, 0, 3.4, 0.16, 4.4);
  for (const [x, z] of [[-1.5, -2], [1.5, -2], [-1.5, 2], [1.5, 2]]) kit.box(M.pine, x, 0.5, z, 0.16, 1.0, 0.16);
  kit.cyl(M.pine, 0, 2.25, -0.3, 1.15, 1.15, 3.4, 18, Math.PI / 2);
  kit.cyl(M.metal, 0, 2.25, -0.3, 1.18, 1.18, 0.1, 18, Math.PI / 2);
  kit.cyl(M.snow, 0, 2.55, -0.3, 1.05, 1.05, 3.45, 18, Math.PI / 2, 0, 0);
  kit.cyl(M.wood, 0, 2.25, 1.38, 1.1, 1.1, 0.06, 18, Math.PI / 2);
  kit.box(M.windowWarm, 0, 2.05, 1.42, 0.6, 1.4, 0.04);
  kit.cyl(M.metal, 0.6, 3.6, -1.2, 0.08, 0.08, 1.3, 8);
  for (let i = 0; i < 5; i++) kit.box(M.deck, 0, 0.9 - i * 0.2, 2.5 + i * 0.32, 1.0, 0.08, 0.3);
  for (const x of [-1.6, -0.6, 0.6, 1.6]) kit.box(M.lamp, x, 1.65, 2.15, 0.12, 0.16, 0.12);
  kit.box(M.pine, 0, 1.6, 2.2, 3.4, 0.08, 0.08);
  kit.cyl(M.water, 0, 0.02, 4.6, 0.9, 0.9, 0.04, 20);
  return { group: kit.build(), top: 4.6, smoke: [[0.6, 4.3, -1.2]] };
}

/**
 * WS-25: Finnish laavu (open-front fire shelter), log construction. Frame: open gable front = +z, back wall at
 * -z, ridge along z. Log side and back walls (crossed corners), open front with a tie beam and `posts` (x of the
 * free-standing front posts), gable roof with overhangs and snow, U-shaped benches inside, optional woodpile,
 * low log wing walls in front of the side bays, and the fire ring in front of the opening (`fireZ`).
 * Every part is a param so a different laavu is data, not code. Defaults: Aurora Village refs (round 5).
 */
export function laavu(M, p = {}) {
  const {
    W = 5.6, D = 3.4, eaveH = 1.9, ridgeH = 3.3, log = 0.1,
    overFront = 0.7, overBack = 0.3, overSide = 0.35,
    posts = [-0.95, 0.95], benches = true, woodpile = 'right',
    wingH = 0.95, wingIn = 1.15, wingZ = 0.25, fire = true, fireZ = null, fireR = 0.45,
  } = p;
  const kit = new Kit();
  const hx = W / 2, hz = D / 2, step = log * 1.9, rows = Math.max(3, Math.round(eaveH / step));
  // log walls: sides (along z) and back (along x), rows offset by half a log so the corners cross
  for (let i = 0; i < rows; i++) {
    const y = log + i * step;
    for (const s of [-1, 1]) kit.cyl(M.wood, s * hx, y + (i % 2 ? log : 0), -0.15 / 2, log, log, D + 0.3 - 0.15, 8, Math.PI / 2);
    kit.cyl(M.wood, 0, y + (i % 2 ? 0 : log), -hz, log, log, W + 0.3, 8, 0, Math.PI / 2);
  }
  const wallTop = log + (rows - 1) * step + 2 * log;
  // warm planed lining inside (the lit interior in the refs)
  for (const s of [-1, 1]) kit.box(M.pine, s * (hx - log - 0.02), wallTop / 2, 0, 0.03, wallTop, D - 2 * log);
  kit.box(M.pine, 0, wallTop / 2, -hz + log + 0.02, W - 2 * log, wallTop, 0.03);
  // back gable triangle, front tie beam, front posts
  kit.slab(M.wood, [-hx, wallTop, -hz + log], [hx, wallTop, -hz + log], [0, ridgeH, -hz + log], [0, ridgeH, -hz + log], 2 * log);
  kit.box(M.pine, 0, wallTop - 0.1, hz - 0.05, W + 0.2, 0.2, 0.18);
  for (const x of posts) kit.cyl(M.pine, x, wallTop / 2, hz - 0.05, 0.12, 0.13, wallTop, 8);
  // roof: two pitches, ridge along z; pine underside, dark edge, snow on top
  const z0 = -hz - overBack, z1 = hz + overFront, ex = hx + overSide, drop = ((ridgeH - wallTop) / hx) * overSide;
  const eY = wallTop - drop;
  for (const s of [-1, 1]) {
    const a = [0, ridgeH + 0.14, z0], b = [0, ridgeH + 0.14, z1], c = [s * ex, eY + 0.14, z1], d = [s * ex, eY + 0.14, z0];
    kit.slab(M.roofEdge, a, b, c, d, 0.12);
    kit.slab(M.pine, [0, ridgeH + 0.01, z0], [0, ridgeH + 0.01, z1], [s * ex, eY + 0.01, z1], [s * ex, eY + 0.01, z0], 0.03);
    kit.slab(M.snow, [0, ridgeH + 0.38, z0 - 0.05], [0, ridgeH + 0.38, z1 + 0.05], [s * (ex + 0.05), eY + 0.36, z1 + 0.05], [s * (ex + 0.05), eY + 0.36, z0 - 0.05], 0.24);
    kit.rod(M.pine, [0, ridgeH, hz], [s * hx, wallTop, hz], 0.07, 6); // front rafter, seen in the open gable
  }
  if (benches) {
    kit.box(M.pine, 0, 0.45, -hz + log + 0.3, W - 2 * log - 0.1, 0.07, 0.5);
    for (const s of [-1, 1]) kit.box(M.pine, s * (hx - log - 0.3), 0.45, 0.1, 0.5, 0.07, D - 1.0);
  }
  if (woodpile) { // stacked firewood against the back wall (left-side ref: back right corner)
    const s = woodpile === 'left' ? -1 : 1;
    for (let r = 0; r < 4; r++) for (let k = 0; k < 6 - r; k++) kit.cyl(M.deck, s * (hx - log - 0.55) - (k - (5 - r) / 2) * 0.13, 0.6 + r * 0.11, -hz + log + 0.25, 0.06, 0.06, 0.4, 6, Math.PI / 2);
  }
  if (wingH > 0) { // low log walls in front of the side bays, snow on top
    const len = hx - wingIn, cx = (hx + wingIn) / 2;
    for (const s of [-1, 1]) {
      for (let i = 0; i < Math.max(2, Math.round(wingH / step)); i++) kit.cyl(M.wood, s * cx, log + i * step, hz + wingZ, log, log, len, 8, 0, Math.PI / 2);
      kit.box(M.snow, s * cx, wingH + 0.06, hz + wingZ, len + 0.1, 0.14, 0.3);
    }
  }
  const smoke = [];
  if (fire) {
    // low stone ring with embers, in the centre bay on the opening line (half under the eave), as in the front ref
    const fz = fireZ ?? hz;
    for (let k = 0; k < 11; k++) {
      const a = (k / 11) * Math.PI * 2;
      kit.box(M.plinth, Math.cos(a) * fireR, 0.08, fz + Math.sin(a) * fireR, 0.2, 0.16, 0.16, -a + (k % 3) * 0.3);
    }
    kit.cyl(M.post, 0, 0.03, fz, fireR - 0.08, fireR - 0.08, 0.06, 12); // ash bed
    kit.cyl(M.led, 0, 0.08, fz, fireR * 0.45, fireR * 0.6, 0.06, 10); // ember glow
    for (const a of [0.4, 2.5, 4.6]) kit.cyl(M.deck, Math.cos(a) * 0.12, 0.13, fz + Math.sin(a) * 0.12, 0.045, 0.045, 0.5, 6, Math.PI / 2, 0, a); // burning logs
    smoke.push([0, 0.6, fz]);
  }
  return { group: kit.build(), top: ridgeH + 0.5, smoke, plan: [-ex - 0.3, ex + 0.3, z0 - 0.3, z1 + 0.8] };
}

/** Log bridge over the channel. */
export function logBridge(M) {
  const kit = new Kit();
  for (let i = -2; i <= 2; i++) kit.cyl(M.pine, 0, 0.55, i * 0.42, 0.21, 0.21, 12, 8, 0, Math.PI / 2);
  for (const s of [-1, 1]) {
    kit.cyl(M.pine, 0, 1.45, s * 1.15, 0.08, 0.08, 12, 6, 0, Math.PI / 2);
    for (let x = -5.5; x <= 5.5; x += 2.75) kit.cyl(M.pine, x, 1.0, s * 1.15, 0.08, 0.08, 1.0, 6);
  }
  kit.box(M.snow, 0, 0.82, 0, 12, 0.1, 1.9);
  return { group: kit.build(), top: 2.5, smoke: [] };
}

/** Panorama sauna terrace: deck, square hot tub with cover, fence and bench. */
export function jacuzziDeck(M) {
  const kit = new Kit();
  kit.box(M.deck, 0, 0.25, 0, 6.5, 0.18, 5.0);
  kit.box(M.wood, -1.2, 0.75, 0.4, 2.3, 0.85, 2.3);
  kit.box(M.water, -1.2, 1.15, 0.4, 2.0, 0.08, 2.0);
  kit.box(M.woodGrey, -1.2, 1.35, -0.95, 2.3, 0.6, 0.12, 0, -0.5);
  kit.box(M.pine, 1.6, 0.6, -1.9, 1.6, 0.1, 0.45);
  for (let x = -3.2; x <= 3.2; x += 0.8) kit.box(M.pine, x, 0.85, -2.45, 0.08, 1.1, 0.08);
  kit.box(M.pine, 0, 1.35, -2.45, 6.5, 0.1, 0.1);
  kit.box(M.lamp, 3.0, 1.5, -2.45, 0.14, 0.2, 0.14);
  return { group: kit.build(), top: 2, smoke: [] };
}

/** Small hexagonal grill kota: low log walls, steep shingle roof, smoke cap. */
export function grillKota(M) {
  const kit = new Kit();
  const r = 2.4, wallH = 1.25, roofH = 3.2;
  const hex = Array.from({ length: 6 }, (_, k) => [Math.cos((k * Math.PI) / 3) * r, Math.sin((k * Math.PI) / 3) * r]);
  kit.ring(M.log || M.wood, hex, 0, wallH, 0.16); // WS-37: log texture under a materials look
  const eave = Array.from({ length: 6 }, (_, k) => [Math.cos((k * Math.PI) / 3) * (r + 0.45), Math.sin((k * Math.PI) / 3) * (r + 0.45)]);
  for (let i = 0; i < 6; i++) {
    const j = (i + 1) % 6;
    const q = [[eave[i][0], wallH - 0.15, eave[i][1]], [eave[j][0], wallH - 0.15, eave[j][1]], [0, wallH + roofH, 0], [0, wallH + roofH, 0]];
    kit.tris(M.roofEdge, [[q[0], q[1], q[2]]]);
    kit.tris(M.snow, [[[q[0][0], q[0][1] + 0.25, q[0][2]], [q[1][0], q[1][1] + 0.25, q[1][2]], [0, wallH + roofH + 0.2, 0]]]);
  }
  kit.cyl(M.metal, 0, wallH + roofH + 0.35, 0, 0.18, 0.25, 0.6, 8);
  kit.box(M.pine, r - 0.05, 0.95, 0, 0.1, 1.6, 0.9);
  return { group: kit.build(), top: wallH + roofH + 1, smoke: [[0, wallH + roofH + 0.7, 0]] };
}

// WS-5: neutral round shelter for an unidentified `building=roof` structure (no walls, fire, lights or
// smoke: nothing that needs a reference). Conical roof on posts; r defaults to the footprint size.
/** Open round shelter: ring of posts, low conical roof. Params: r (m), eaveH, roofH, posts. */
export function roundShelter(f, M, { r: rp = null, eaveH = 2.2, roofH = 1.8, posts = 8 } = {}) {
  const kit = new Kit();
  const r = rp || Math.max(2.5, Math.min(f.L, f.W) / 2 - 0.4);
  for (let i = 0; i < posts; i++) {
    const a = (i / posts) * Math.PI * 2;
    kit.box(M.pine, Math.cos(a) * (r - 0.35), eaveH / 2, Math.sin(a) * (r - 0.35), 0.18, eaveH, 0.18);
  }
  kit.cyl(M.roofEdge, 0, eaveH + roofH / 2, 0, 0.15, r + 0.3, roofH, 24);
  kit.cyl(M.snow, 0, eaveH + roofH / 2 + 0.22, 0, 0.12, r + 0.32, roofH, 24);
  return { group: kit.build(), top: eaveH + roofH + 0.4, smoke: [] };
}
// WS-5 end

// ---- WS-10 terrace kit (begin) ------------------------------------------------------------------
// Generic outdoor works in a local frame (a building's unit frame, or a prop's): plank decks on posts,
// stairs down to the terrain, railings (optionally with lanterns), hot tubs with their cover, benches,
// slatted privacy screens, roundpole fences and ice holes with a ladder. Everything is data in resort.json
// (buildings.<key>.params.terrace, or a prop's params.terrace), each element with its own `src`.
// groundY(x, z) = terrain height in the same frame, so posts and stairs reach the real ground.
// Element `rot` is degrees about +y like props (0 = element length along local +x).
const rotXZ = (deg) => { const r = THREE.MathUtils.degToRad(deg || 0); return [[Math.cos(r), -Math.sin(r)], [Math.sin(r), Math.cos(r)]]; };
function along(kit, mat, a, b, y, w, h) { // horizontal beam from a to b (plan points), centre height y
  const dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz);
  if (len < 1e-3) return;
  kit.box(mat, (a[0] + b[0]) / 2, y, (a[1] + b[1]) / 2, len, h, w, Math.atan2(-dz, dx));
}
/** Rail that follows the ground (`follow: true`): a log fence along a path, posts every ~1.5 m from the ground,
 *  top and mid rails between post tops, `lanterns` hung on the posts (photo count). */
function followRail(kit, M, r, groundY) {
  const h = r.h ?? 0.95, P = [];
  for (let i = 0; i < r.pts.length - 1; i++) {
    const a = r.pts[i], b = r.pts[i + 1], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 1.5));
    for (let k = i ? 1 : 0; k <= n; k++) P.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
  }
  const G = P.map(([x, z]) => groundY(x, z) + (r.base ?? 0));
  P.forEach(([x, z], i) => kit.rod(M.deck, [x, G[i] - 0.2, z], [x, G[i] + h + 0.05, z], 0.06, 6));
  for (let i = 0; i < P.length - 1; i++) for (const f of [1, 0.5]) kit.rod(M.deck, [P[i][0], G[i] + h * f, P[i][1]], [P[i + 1][0], G[i + 1] + h * f, P[i + 1][1]], f === 1 ? 0.06 : 0.045, 6);
  for (let j = 0; j < (r.lanterns || 0); j++) {
    const i = Math.min(P.length - 1, Math.round(((j + 0.5) * P.length) / r.lanterns - 0.5)), [x, z] = P[i];
    kit.box(M.lamp, x, G[i] + h - 0.22, z, 0.13, 0.18, 0.13);
  }
}
/** VIS-99: an ice hole cut into thick river ice (iceHoles[].ice = { t, seed, rough, stretch, lip, lift, freeboard }).
 *  Organic outline (a few seeded harmonics, `stretch` along the hole's rot); the cut edge stands `lift` m proud of the
 *  highest ground under the hole (the drawn river surface; the bank may slope into it) and the snow on the ice falls
 *  from it to the ground over `lip` m, a clean vertical cut face of ice goes down from the edge, and dark water lies
 *  `freeboard` m below the ice top. No raised ring, no pool edge. */
function iceCut(kit, M, o, g, r, ex, ez, groundY) {
  const c = o.ice && typeof o.ice === 'object' ? o.ice : {}, N = 40, s = c.stretch ?? 1, rough = c.rough ?? 0.18, lip = c.lip ?? 0.7;
  M.iceCut ||= new THREE.MeshStandardNodeMaterial({ color: 0x8db4c8, roughness: 0.18, metalness: 0.05, emissive: 0x16303c, emissiveIntensity: 0.25 });
  // dark open water (Aurora's water.colors.open #0b1820) with a soft sky sheen. No fog: the low-ground mist (the haze's
  // fog node) lies thickest in a river channel and tinted the dark water brown at any distance (owner 2026-11-05: 'why is
  // it brown?'); over white snow the same mist does not show
  M.holeWater ||= new THREE.MeshStandardNodeMaterial({ color: 0x0b1820, roughness: 0.2, metalness: 0, envMapIntensity: 0.4, fog: false });
  let a = (c.seed ?? 1) * 2654435761 >>> 0; // seeded so the outline never changes between loads
  const rnd = () => { a = (a + 0x6d2b79f5) >>> 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t ^= t + Math.imul(t ^ (t >>> 7), 61 | t); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const ph = Array.from({ length: 5 }, () => rnd() * Math.PI * 2);
  const at = (th, rho) => { const u = Math.cos(th) * rho * s, v = (Math.sin(th) * rho) / s; return [o.pos[0] + ex[0] * u + ez[0] * v, o.pos[1] + ex[1] * u + ez[1] * v]; };
  const P = [], Q = [];
  for (let i = 0; i < N; i++) {
    const th = (i / N) * Math.PI * 2;
    const rho = r * (1 + rough * (0.6 * Math.sin(2 * th + ph[0]) + 0.35 * Math.sin(3 * th + ph[1]) + 0.2 * Math.sin(5 * th + ph[2])));
    const w = lip * (1 + 0.3 * Math.sin(2 * th + ph[3]) + 0.15 * Math.sin(4 * th + ph[4]));
    P.push(at(th, rho)); Q.push(at(th, rho + w));
  }
  const top = Math.max(g, ...P.map((p) => groundY(...p))) + (c.lift ?? 0.1), water = top - (c.freeboard ?? 0.08), bottom = top - (c.t ?? 0.45);
  const y3 = (p, y) => [p[0], y, p[1]];
  const face = (tri, want) => { // orient a triangle so its normal points along `want` (x, y, z)
    const [A, B, C] = tri, n = [(B[1] - A[1]) * (C[2] - A[2]) - (B[2] - A[2]) * (C[1] - A[1]), (B[2] - A[2]) * (C[0] - A[0]) - (B[0] - A[0]) * (C[2] - A[2]), (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0])];
    return n[0] * want[0] + n[1] * want[1] + n[2] * want[2] >= 0 ? tri : [A, C, B];
  };
  const snow = [], cut = [], wet = [];
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N, mx = (P[i][0] + P[j][0]) / 2 - o.pos[0], mz = (P[i][1] + P[j][1]) / 2 - o.pos[1];
    const out = [mx, 0, mz], inw = [-mx, 0, -mz];
    const p0 = y3(P[i], top), p1 = y3(P[j], top), q0 = y3(Q[i], groundY(...Q[i]) - 0.02), q1 = y3(Q[j], groundY(...Q[j]) - 0.02);
    snow.push(face([p0, p1, q1], [out[0], 1, out[2]]), face([p0, q1, q0], [out[0], 1, out[2]])); // the snow on the ice, down to the ground
    const b0 = y3(P[i], bottom), b1 = y3(P[j], bottom), w0 = y3(P[i], top - 0.05), w1 = y3(P[j], top - 0.05);
    snow.push(face([p0, p1, w1], inw), face([p0, w1, w0], inw)); // the snow layer seen in the cut
    cut.push(face([w0, w1, b1], inw), face([w0, b1, b0], inw)); // the clean cut face of the ice
    wet.push(face([y3(o.pos, water), y3(P[i], water), y3(P[j], water)], [0, 1, 0]));
  }
  kit.tris(M.snow, snow); kit.tris(M.iceCut, cut); kit.tris(M.holeWater, wet);
  return top;
}
export function terrace(M, p = {}, groundY = () => 0) {
  const kit = new Kit(), steam = [];
  M.tubWater ||= new THREE.MeshStandardNodeMaterial({ color: 0x7fd3db, emissive: 0x2a9aa8, emissiveIntensity: 0.45, roughness: 0.08 });
  const post = (x, z, yTop, s = 0.12) => { const g = groundY(x, z); if (yTop - g > 0.05) kit.box(M.wood, x, (g + yTop) / 2, z, s, yTop - g, s); };
  let top = 0;
  for (const d of p.decks || []) {
    const y = d.top ?? 0.4, t = d.t ?? 0.1, poly = d.poly;
    kit.prism(M.deck, poly, y - t, t, d.hole); // 2026-10-18: `hole` = a cut-out (a tub sunk in the deck)
    if (d.snow) kit.prism(M.snow, insetConvex(poly, 0.05), y, 0.06, d.hole && insetConvex(d.hole, -0.05));
    const inner = insetConvex(poly, 0.15);
    for (let i = 0; i < inner.length; i++) {
      const a = inner[i], b = inner[(i + 1) % inner.length], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 1.8));
      for (let k = 0; k < n; k++) post(a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n, y - t);
    }
    top = Math.max(top, y);
  }
  for (const s of p.stairs || []) { // from = top landing edge centre (height `top`), to = foot centre on the ground
    const w = s.w ?? 1.0, y0 = s.top ?? 0.4, g1 = s.bottom ?? groundY(...s.to), drop = y0 - g1; // bottom: foot on a lower deck
    if (drop <= 0.05) continue;
    const n = Math.max(2, Math.round(drop / 0.18)), dx = (s.to[0] - s.from[0]) / n, dz = (s.to[1] - s.from[1]) / n;
    const len = Math.hypot(s.to[0] - s.from[0], s.to[1] - s.from[1]), ux = (s.to[0] - s.from[0]) / len, uz = (s.to[1] - s.from[1]) / len;
    const ry = Math.atan2(-uz, ux), nx = -uz * (w / 2), nz = ux * (w / 2);
    for (let i = 0; i < n; i++) kit.box(M.deck, s.from[0] + dx * (i + 0.5), y0 - (drop * (i + 1)) / n + 0.03, s.from[1] + dz * (i + 0.5), Math.hypot(dx, dz) + 0.04, 0.05, w, ry);
    for (const sd of [1, -1]) {
      const a = [s.from[0] + sd * nx, y0 - 0.05, s.from[1] + sd * nz], b = [s.to[0] + sd * nx, g1, s.to[1] + sd * nz];
      kit.rod(M.wood, a, b, 0.06);
      // VIS-155: `rails` true (default) = a handrail on both sides; false = none; 'right' | 'left' = on that side only,
      // as seen walking DOWN the stair (from -> to); sd 1 is the walker's right
      if (s.rails !== false && !(s.rails === 'right' && sd < 0) && !(s.rails === 'left' && sd > 0)) {
        kit.rod(M.deck, [a[0], a[1] + 0.95, a[2]], [b[0], b[1] + 0.95, b[2]], 0.035);
        for (const q of [a, b]) kit.box(M.deck, q[0], q[1] + 0.48, q[2], 0.08, 0.95, 0.08);
      }
    }
    top = Math.max(top, y0);
  }
  for (const r of p.rails || []) {
    if (r.follow) { followRail(kit, M, r, groundY); continue; } // fence on sloping ground (posts from the ground)
    const y = r.base ?? 0.4, h = r.h ?? 0.95, pts = r.pts;
    let total = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1], len = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.max(1, Math.ceil(len / 1.5));
      for (let k = 0; k <= n; k++) { const x = a[0] + ((b[0] - a[0]) * k) / n, z = a[1] + ((b[1] - a[1]) * k) / n; kit.box(M.deck, x, y + h / 2, z, 0.08, h, 0.08); }
      along(kit, M.deck, a, b, y + h, 0.12, 0.05);
      along(kit, M.deck, a, b, y + h * 0.5, 0.05, 0.08);
      total += len;
    }
    for (let i = 0; i < (r.lanterns || 0); i++) { // lanterns evenly spaced along the top rail (photo count)
      let s = (total * (i + 0.5)) / r.lanterns, j = 0;
      for (; j < pts.length - 2; j++) { const l = Math.hypot(pts[j + 1][0] - pts[j][0], pts[j + 1][1] - pts[j][1]); if (s <= l) break; s -= l; }
      const a = pts[j], b = pts[j + 1], l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
      kit.box(M.lamp, a[0] + ((b[0] - a[0]) * s) / l, y + h - 0.14, a[1] + ((b[1] - a[1]) * s) / l, 0.13, 0.18, 0.13);
    }
  }
  for (const t of p.tubs || []) { // square spa: grey cabinet, white shell rim, lit water, cover folded up on one side
    const [sx, sz] = t.size || [2.3, 2.3], y0 = t.base ?? 0.4, h = t.rimH ?? 0.9;
    const [ex, ez] = rotXZ(t.rot), ry = THREE.MathUtils.degToRad(t.rot || 0);
    const P = (a, b) => [t.pos[0] + a * ex[0] + b * ez[0], t.pos[1] + a * ex[1] + b * ez[1]];
    const c = P(0, 0);
    kit.box(M.woodGrey, c[0], y0 + (h - 0.25) / 2, c[1], sx - 0.06, h - 0.25, sz - 0.06, ry);
    for (const [a, b, lx, lz] of [[0, sz / 2 - 0.12, sx, 0.24], [0, -sz / 2 + 0.12, sx, 0.24], [sx / 2 - 0.12, 0, 0.24, sz - 0.48], [-sx / 2 + 0.12, 0, 0.24, sz - 0.48]]) {
      const q = P(a, b);
      kit.box(M.trim, q[0], y0 + h - 0.06, q[1], lx, 0.12, lz, ry);
    }
    kit.box(M.tubWater, c[0], y0 + h - 0.12, c[1], sx - 0.4, 0.04, sz - 0.4, ry);
    if (t.light === 'cycle') fx.cycleTubLight(M.tubWater); // colour-cycling underwater light (user statement)
    if (t.steam) steam.push([c[0], y0 + h, c[1], t.steam === true ? 1 : t.steam]);
    for (const [a, b, lx, lz] of [[0, sz / 2 - 0.06, sx, 0.12], [0, -sz / 2 + 0.06, sx, 0.12], [sx / 2 - 0.06, 0, 0.12, sz], [-sx / 2 + 0.06, 0, 0.12, sz]]) {
      const q = P(a, b); kit.box(M.woodGrey, q[0], y0 + h - 0.25, q[1], lx, 0.26, lz, ry); // cabinet skirt up to the rim
    }
    if (t.cover) { // folded cover standing on the named side of the tub (tub frame: +x / -x / +z / -z)
      const sd = t.cover[0] === '-' ? -1 : 1, onX = t.cover[1] === 'x';
      const q = onX ? P(sd * (sx / 2 + 0.1), 0) : P(0, sd * (sz / 2 + 0.1));
      kit.box(M.roofEdge, q[0], y0 + h + 0.5, q[1], onX ? 0.12 : sx, 1.05, onX ? sz : 0.12, ry);
    }
    top = Math.max(top, y0 + h + 1);
  }
  for (const b of p.benches || []) {
    const len = b.len ?? 1.6, y0 = b.base ?? 0.4, [ex] = rotXZ(b.rot), ry = THREE.MathUtils.degToRad(b.rot || 0);
    kit.box(M.deck, b.pos[0], y0 + 0.43, b.pos[1], len, 0.05, 0.42, ry);
    for (const s of [-1, 1]) kit.box(M.wood, b.pos[0] + ex[0] * s * (len / 2 - 0.12), y0 + 0.2, b.pos[1] + ex[1] * s * (len / 2 - 0.12), 0.07, 0.4, 0.38, ry);
  }
  for (const s of p.screens || []) { // slatted privacy screen: two posts, horizontal slats
    const w = s.w ?? 1.3, h = s.h ?? 1.8, y0 = s.base ?? 0.4, [ex] = rotXZ(s.rot), ry = THREE.MathUtils.degToRad(s.rot || 0);
    for (const sd of [-1, 1]) kit.box(M.wood, s.pos[0] + (ex[0] * sd * w) / 2, y0 + h / 2, s.pos[1] + (ex[1] * sd * w) / 2, 0.09, h, 0.09, ry);
    for (let y = y0 + 0.25; y < y0 + h - 0.05; y += 0.16) kit.box(M.deck, s.pos[0], y, s.pos[1], w, 0.09, 0.03, ry);
  }
  for (const f of p.fences || []) { // roundpole fence (riukuaita): post pairs every 1.6 m, poles leaning ~40 deg along the line
    const h = f.h ?? 1.5, pts = f.pts;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1], len = Math.hypot(b[0] - a[0], b[1] - a[1]), ux = (b[0] - a[0]) / len, uz = (b[1] - a[1]) / len;
      for (let s = 0; s <= len; s += 1.6) {
        const x = a[0] + ux * s, z = a[1] + uz * s, g = groundY(x, z);
        for (const sd of [-1, 1]) kit.rod(M.woodGrey, [x - uz * sd * 0.09, g, z + ux * sd * 0.09], [x - uz * sd * 0.09, g + h + 0.15, z + ux * sd * 0.09], 0.05);
      }
      for (let s = 0; s < len - h * 1.1; s += 0.3) {
        const x = a[0] + ux * s, z = a[1] + uz * s, x2 = x + ux * h * 1.15, z2 = z + uz * h * 1.15;
        kit.rod(M.woodGrey, [x, groundY(x, z) + 0.05, z], [x2, groundY(x2, z2) + h, z2], 0.045);
      }
    }
  }
  for (const o of p.iceHoles || []) { // open water in the ice, with a stainless ladder on the `ladderAt` side (photo)
    const g = Math.max(groundY(...o.pos), o.level ?? -Infinity), r = o.r ?? 0.8, [ex, ez] = rotXZ(o.rot); // level: water surface (frame y)
    iceCut(kit, M, o, g, r, ex, ez, groundY); // VIS-99: always a hole cut in thick ice, organic outline (no rim, no round pool: check `package`)
    if (o.ladder) for (const sd of [-1, 1]) {
      const zz = o.ladderAt ?? -r * 0.8, lx = o.pos[0] + ex[0] * sd * 0.25 + ez[0] * zz, lz = o.pos[1] + ex[1] * sd * 0.25 + ez[1] * zz;
      const lt = o.ladderTop ?? 0.75; // VIS-99: the rails bend over at this height (a deck beside the hole: its top)
      kit.rod(M.metal, [lx, g - 0.3, lz], [lx, g + lt, lz], 0.025);
      kit.rod(M.metal, [lx, g + lt, lz], [lx + ez[0] * 0.35 * Math.sign(zz), g + lt, lz + ez[1] * 0.35 * Math.sign(zz)], 0.025);
    }
  }
  return { group: kit.build(), top: top + 1, smoke: [], steam };
}

/** Barrel sauna on a terrace (WS-10): barrel on its deck at `base`, door end +z, stove pipe at `chimneyZ`
 *  along the axis (+ = towards the door); deck, stairs, rails and ice hole come from the terrace kit.
 *  Door end (+z, viewer facing it: right = +x): glazed door, a narrow window each side, sign board upper right,
 *  round vent upper left. `backWindow { w, h, y }` = a small window in the rear end (-z), y above the barrel bottom.
 *  Snow lies as a crescent cap on the crown (no snow on the end walls).
 *  WS-62 (optional; defaults = the look above): `r` 1.15 barrel radius, `doorGlass` 'panel' (tall narrow pane) |
 *  'top' (upper half glazed in 2 x 3 small panes), `sideWindows` true, `sign` 'right' | 'left' | false, `vent` true,
 *  `chimney` true (false = no stove pipe drawn and no smoke point: only with a photo of the pipe), `ends` / `leaf`
 *  material keys of the end walls / the door leaf (default 'wood', dark stain). */
function barrelOnTerrace(M, p, groundY) {
  const kit = new Kit(), y = p.base ?? 0.4, r = p.r ?? 1.15, len = p.len ?? 3.4, cz = p.chimneyZ ?? -1.2, cy = y + r + 0.12;
  kit.cyl(M[p.shell] || M.pine, 0, cy, 0, r, r, len, 18, Math.PI / 2); // shell: material key (stained barrels: 'wood')
  for (const z of [0, -len * 0.32, len * 0.32]) kit.cyl(M.metal, 0, cy, z, r + 0.03, r + 0.03, 0.06, 18, Math.PI / 2); // steel hoops
  for (const s of [-1, 1]) kit.cyl(M[p.ends] || M.wood, 0, cy, s * (len / 2 + 0.01), r + 0.04, r + 0.04, 0.1, 18, Math.PI / 2); // stained end walls + rims (WS-62: `ends` material key)
  const rs = p.roof ? r + 0.06 : r + 0.01;
  if (p.roof) { // black roofing-felt shell over the top of the barrel (user/10.png, Barrel-Sauna-15)
    const g = new THREE.CylinderGeometry(r + 0.05, r + 0.05, len + 0.12, 18, 1, true, -Math.PI * 0.42, Math.PI * 0.84);
    g.rotateX(-Math.PI / 2); g.translate(0, cy, 0); // axis along z, shell centred on +y
    kit.add(M.roofEdge, g);
  }
  { // snow cap: between the roof arc and the same arc lifted by `t` (thickest on the crown, thin at the sides)
    const t = 0.16, a = 0.85, n = 14, pts = [];
    for (let i = 0; i <= n; i++) { const q = Math.PI / 2 - a + (2 * a * i) / n; pts.push(new THREE.Vector2(Math.cos(q) * rs, Math.sin(q) * rs)); }
    for (let i = n; i >= 0; i--) { const q = Math.PI / 2 - a + (2 * a * i) / n; pts.push(new THREE.Vector2(Math.cos(q) * rs, Math.sin(q) * rs + t * Math.sin(q) ** 4)); }
    const g = new THREE.ExtrudeGeometry(new THREE.Shape(pts), { depth: len + 0.1, bevelEnabled: false });
    g.translate(0, cy, -(len + 0.1) / 2);
    kit.add(M.snow, g);
  }
  // Door end (+z): 2025 summer clips (Barrel Sauna - 4) + 2026-01-23 path clips (DJI_..0129): centred glazed door,
  // narrow side windows, "PRIVATE" board upper right, round vent upper left.
  const ez = len / 2 + 0.06, yb = cy - r; // end-wall face, barrel bottom
  for (const s of [-1, 1]) kit.box(M.post, s * 0.4, yb + 1.05, ez + 0.01, 0.08, 1.9, 0.05); // door jambs
  kit.box(M.post, 0, yb + 2.0, ez + 0.01, 0.88, 0.08, 0.05); // head
  kit.box(M[p.leaf] || M.wood, 0, yb + 1.05, ez + 0.02, 0.72, 1.85, 0.03); // leaf (WS-62: `leaf` material key)
  if (p.doorGlass === 'top') { // WS-62: upper half glazed, 2 x 3 panes with muntins
    kit.box(M.windowWarm, 0, yb + 1.5, ez + 0.04, 0.5, 0.78, 0.02);
    kit.box(M.wood, 0, yb + 1.5, ez + 0.055, 0.04, 0.78, 0.015);
    for (const dy of [-0.13, 0.13]) kit.box(M.wood, 0, yb + 1.5 + dy, ez + 0.055, 0.5, 0.035, 0.015);
  } else kit.box(M.windowWarm, 0, yb + 1.15, ez + 0.04, 0.22, 1.25, 0.02); // tall glazed panel
  if (p.sideWindows !== false) for (const s of [-1, 1]) {
    kit.box(M.post, s * 0.68, yb + 1.25, ez + 0.01, 0.26, 1.02, 0.03); // side window frame
    kit.box(M.windowWarm, s * 0.68, yb + 1.25, ez + 0.03, 0.16, 0.9, 0.02);
  }
  if (p.sign !== false) { // sign board beside the door (viewer facing the door: right = +x)
    const sx = p.sign === 'left' ? -0.62 : 0.42, sw = p.sign === 'left' ? 0.3 : 0.62, sy = p.sign === 'left' ? yb + 1.55 : yb + 2.12;
    kit.box(M.post, sx, sy, ez + 0.03, sw, p.sign === 'left' ? 0.14 : 0.24, 0.04); // border
    kit.box(M.trim, sx, sy, ez + 0.05, sw - 0.06, p.sign === 'left' ? 0.09 : 0.18, 0.02); // board
  }
  if (p.vent !== false) kit.cyl(M.metal, -0.62, yb + 1.95, ez + 0.03, 0.12, 0.12, 0.05, 12, Math.PI / 2); // round vent
  if (p.backWindow) { // small rear window (-z)
    const { w = 0.35, h = 0.35, y: wy = 1.5 } = p.backWindow, z = -ez;
    kit.box(M.post, 0, yb + wy, z - 0.01, w + 0.1, h + 0.1, 0.05);
    kit.box(M.windowWarm, 0, yb + wy, z - 0.03, w, h, 0.02);
  }
  for (const sx of [-1, 1]) kit.box(M.wood, sx * 0.6, y + 0.07, 0, 0.12, 0.14, len - 0.3); // cradle beams
  const pipe = p.chimney !== false; // WS-62: a stove pipe only with a photo of it
  if (pipe) kit.cyl(M.metal, p.chimneyX ?? 0.35, y + 2 * r + 0.45, cz, 0.08, 0.08, 1.3, 8);
  if (p.lights) { // VIS-174: icicle fairy lights hanging from the shell (Lumo 'Sauna - 3'): strands every `gap` m along the
    // sides (`sides`: 'both' | 'left' | 'right', viewer facing the door) and over the door end's rim (`front`), up to `drop` m long;
    // `glow` = their strength (x M.lamp's)
    const { gap = 0.14, drop = 0.5, sides = 'both', front = false, glow = 1 } = p.lights, a = 0.6, hang = [], bulbMat = glowScaled(M, 'lamp', glow);
    for (const s of [-1, 1]) if (sides === 'both' || (sides === 'right') === s > 0) for (let z = -len / 2 + 0.1; z < len / 2; z += gap) hang.push([s, cy + r * Math.sin(a), z]);
    if (front) for (let q = 0.35; q < Math.PI - 0.3; q += gap / r) hang.push([r * Math.cos(q), cy + r * Math.sin(q) - 0.02, ez + 0.03, 1]);
    hang.forEach(([x, hy, z, rim], i) => { // a side strand keeps 4 cm off the shell as the barrel widens below it
      const n = 1 + ((i * 7) % 4), d = (drop * n) / 4;
      for (let k = 0; k < n; k++) { const yy = hy - 0.06 - (d * k) / n; kit.add(bulbMat, new THREE.OctahedronGeometry(0.03).translate(rim ? x : x * (Math.sqrt(Math.max(0, r * r - (yy - cy) ** 2)) + 0.04), yy, z)); }
    });
  }
  const t = terrace(M, p.terrace || {}, groundY);
  const group = kit.build();
  group.add(t.group);
  return { group, top: Math.max(y + 2 * r + (pipe ? 1.2 : 0.3), t.top), smoke: pipe ? [[p.chimneyX ?? 0.35, y + 2 * r + 1.15, cz]] : [] };
}
/** Per-frame model effects (WS-10): main.js calls fx.tick(dt, t) every frame. */
export const fx = {
  tickers: new Set(),
  tick(dt, t) { for (const f of this.tickers) f(dt, t); },
  drawers: new Set(),
  /** VIS-267 step 3: main.js, on a frame that will be drawn and is not a rest probe, right before the render: what
   *  rests with the map (the steam) moves on by the time it is owed */
  drawn() { for (const f of this.drawers) f(); },
  /** Classic spa light: the water slowly fades through the colour wheel (~40 s per cycle). */
  cycleTubLight(mat) {
    if (mat.userData.cycling) return;
    mat.userData.cycling = true;
    const c = new THREE.Color(), white = new THREE.Color(0xffffff);
    this.tickers.add((dt, t) => { c.setHSL((t / 40) % 1, 0.8, 0.5); mat.emissive.copy(c); mat.color.copy(c).lerp(white, 0.4); });
  },
  /** Faint steam over hot water: a few soft points rising ~1.5 m, barely visible (sources: [x, y, z, strength]).
   *  VIS-267 step 3: drawn only where a source's puffs can be seen (three culls a sprite by its own position: each
   *  source's sphere instead, in every pass). It rests with the map (the owner, 2026-10-26, as the chimney smoke): the
   *  ticker only owes the time and fx.drawn() moves the puffs on by at most 0.25 s of it, so they hold while the map rests
   *  and go on without a jump. */
  steam(scene, sources) {
    if (!sources.length) return null;
    // WS-75: the motion is a vertex node (no per-frame upload); WebGPU draws sized points as instanced quads (a Sprite)
    const N = 14, n = sources.length * N, src = new Float32Array(n * 4), seed = new Float32Array(n * 2);
    for (let s = 0; s < sources.length; s++) {
      const [x, y, z, k = 1] = sources[s];
      for (let j = 0; j < N; j++) { const i = s * N + j; src.set([x, y, z, k], i * 4); seed.set([Math.random(), i * 2.39], i * 2); }
    }
    const cv = document.createElement('canvas'); cv.width = cv.height = 64;
    const cx = cv.getContext('2d'), grd = cx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    cx.fillStyle = grd; cx.fillRect(0, 0, 64, 64);
    const time = uniform(0);
    const SA = new THREE.InstancedBufferAttribute(src, 4), S = instancedBufferAttribute(SA), Q = instancedBufferAttribute(new THREE.InstancedBufferAttribute(seed, 2));
    const l = fract(Q.x.add(time.mul(0.18))), a = Q.y; // life 0..1 (rises ~1.5 m, round again), angle round the source
    const mat = new THREE.PointsNodeMaterial({ size: 1.4, map: new THREE.CanvasTexture(cv), transparent: true, opacity: 0.06, depthWrite: false, color: 0xeef3f8 });
    mat.positionNode = vec3(
      S.x.add(cos(a).mul(0.7)).add(sin(time.mul(0.4).add(a)).mul(l).mul(0.4)),
      S.y.add(0.1).add(l.mul(1.5).mul(S.w)),
      S.z.add(sin(a).mul(0.7)).add(cos(time.mul(0.3).add(a)).mul(l).mul(0.4)));
    const pts = new THREE.Sprite(mat);
    pts.count = n; pts.name = 'steam'; pts.userData.clock = time; // (QA: the puffs' clock)
    // a source's puffs: up to 1.1 m round it, 0.1-1.6 m (x strength) over it, each ~0.7 m across (size 1.4 attenuated)
    const balls = sources.map(([x, y, z, k = 1]) => new THREE.Sphere(new THREE.Vector3(x, y + 0.1 + 0.75 * k, z), Math.hypot(1.1, 0.75 * k) + 1));
    pts.intersectsFrustum = (f) => balls.some((b) => f.intersectsSphere(b));
    Object.assign(pts.userData, { sources, gpu: SA, balls }); // the editor rewrites them (lazy/finetune-carry.js fxMoved)
    pts.frustumCulled = false; pts.onAfterRender = () => { pts.frustumCulled = true; delete pts.onAfterRender; }; // culled from its first draw or compile on (the warm start's compileAsync leaves out what is out of the opening view: a stall at first sight)
    scene.add(pts);
    let owed = 0, clock = 0;
    this.tickers.add((dt) => { owed += dt; }); this.drawers.add(() => { clock += Math.min(owed, 0.25); owed = 0; time.value = clock; });
    return pts;
  },
};

/** Octagon + rear gable block (WS-10, glassFront with `rearW`): a full octagon (apothem a, centre xg) with a glass
 *  half-pyramid on the front half and shingle facets behind, plus a narrower rear block (walls rearW wide) under a
 *  steep gable roof with a deep overhang; door centred in the rear gable, lamp and sign above it, a landing with
 *  steps (`entrySteps`), an optional small side window (`sideWindow` left|right, viewer facing the glass). */
function glassOctagon(f, M, p) {
  const kit = new Kit();
  const L = f.L, a = Math.max(2.3, (f.W - 0.25) / 2), xr = -L / 2 + (p.porch ?? 0.45), xg = L / 2 - a;
  const plinth = 0.32, wallH = p.wallH ?? 1.45, H = p.ridgeH || wallH + a, rw = p.rearW / 2, wallR = p.rearWallH ?? wallH; // rearWallH: rear block eave height
  const t45 = Math.tan(Math.PI / 8) * a, oct = octagon(xg, 0, a), rect = [[xr, -rw], [xg, -rw], [xg, rw], [xr, rw]];
  for (const [plan, wh] of [[oct, wallH], [rect, wallR]]) {
    kit.prism(M.plinth, insetConvex(plan, -0.02), 0, plinth);
    kit.ring(M.wood, plan, plinth, wh - plinth, 0.16);
  }
  for (const [x, z] of oct.filter(([x, z]) => !(x < xg && Math.abs(z) < rw))) kit.box(M.post, x, (wallH + plinth) / 2, z, 0.16, wallH - plinth, 0.16);
  for (const z of [-rw, rw]) kit.box(M.post, xr, (wallR + plinth) / 2, z, 0.16, wallR - plinth, 0.16);
  // Rear gable: wall, centred door, lamp + sign above it, landing and steps (photos of the entrance side).
  kit.gable(M.wood, xr + 0.15, [[-rw, 0], [rw, 0], [rw, wallR], [0, H], [-rw, wallR]], 0.15);
  // Entrance (Panorama Sauna - 8..12): dark stained door, glazed upper panel lit from inside, dark frame; wall lamp
  // and a light "SAUNA" sign board with a dark border above it. Seasonal garland left out (decoration, not structure).
  const dy = plinth + 0.05, dx = xr - 0.03;
  kit.box(M.post, dx - 0.02, dy + 0.05, 0, 0.1, 0.1, 1.12); // threshold
  kit.box(M.post, dx - 0.02, dy + 2.06, 0, 0.1, 0.12, 1.12); // head
  for (const s of [-1, 1]) kit.box(M.post, dx - 0.02, dy + 1.05, s * 0.51, 0.1, 2.1, 0.1); // jambs
  kit.box(M.wood, dx - 0.04, dy + 1.0, 0, 0.06, 1.9, 0.9); // leaf
  kit.box(M.post, dx - 0.08, dy + 1.45, 0, 0.03, 0.7, 0.62); // glazing bead
  kit.box(M.windowWarm, dx - 0.1, dy + 1.45, 0, 0.02, 0.6, 0.52); // glazed upper panel, warm light
  kit.box(M.metal, dx - 0.1, dy + 0.95, -0.33, 0.04, 0.04, 0.16); // handle
  kit.box(M.post, dx - 0.06, dy + 2.3, 0, 0.04, 0.24, 0.74); // sign board border
  kit.box(M.trim, dx - 0.085, dy + 2.3, 0, 0.02, 0.18, 0.66); // sign board
  kit.box(M.metal, dx - 0.12, dy + 2.62, 0, 0.18, 0.08, 0.34); // wall lamp housing
  kit.box(M.lamp, dx - 0.14, dy + 2.56, 0, 0.14, 0.05, 0.28); // lamp glow
  const st = p.entrySteps || { w: 2.5, landing: 0.65, steps: 2 };
  kit.box(M.deck, xr - st.landing / 2, plinth - 0.05, 0, st.landing, 0.1, st.w);
  for (let i = 0; i < st.steps; i++) {
    const top = plinth - ((i + 1) * plinth) / (st.steps + 1);
    kit.box(M.deck, xr - st.landing - 0.17 - i * 0.32, top - 0.05, 0, 0.34, 0.1, st.w);
  }
  if (p.sideWindow) {
    const s = p.sideWindow === 'right' ? -1 : 1;
    kit.box(M.glass, xg - a - 0.7, wallH - 0.5, s * (rw + 0.01), 0.5, 0.45, 0.04);
    kit.box(M.post, xg - a - 0.7, wallH - 0.5, s * (rw + 0.005), 0.6, 0.55, 0.03);
  }
  // Steep gable roof over the rear block, ridge running into the octagon apex.
  gableRoof(kit, M, xr - (p.gableOverhang ?? 0.45), xg, rw, wallR, H, { overhang: p.rearEaves ?? 0.5 }); // gableOverhang: rake depth over the entrance
  // Octagon roof: shingle facets on the rear half, glass on the front half, dark eave skirt round the front.
  const yE = wallH + 0.06, apex = [xg, H, 0], ring = (pts) => pts.map(([x, z]) => [x, yE, z]);
  const back = ring([[xg, a], [xg - t45, a], [xg - a, t45], [xg - a, -t45], [xg - t45, -a], [xg, -a]]).map(([x, y, z]) => [xg + (x - xg) * 1.08, y - 0.08, z * 1.08]);
  for (let i = 0; i < back.length - 1; i++) {
    kit.slab(M.roofEdge, back[i], back[i + 1], apex, apex, 0.12);
    const up = (q) => [q[0], q[1] + 0.28, q[2]];
    kit.slab(M.snow, up(back[i]), up(back[i + 1]), up(apex), up(apex), 0.2);
  }
  let front = ring([[xg, a], [xg + t45, a], [L / 2, t45], [L / 2, -t45], [xg + t45, -a], [xg, -a]]);
  if (p.frontEave > 0) {
    const e = p.frontEave, inner = front.map(([x, y, z]) => { const dx = xg - x, dz = -z, d = Math.hypot(dx, dz) || 1; return [x + (dx / d) * e, y + e * 0.55, z + (dz / d) * e]; });
    const outer = front.map(([x, y, z]) => { const dx = x - xg, d = Math.hypot(dx, z) || 1; return [x + (dx / d) * 0.12, y - 0.06, z + (z / d) * 0.12]; });
    for (let i = 0; i < front.length - 1; i++) kit.slab(M.roofEdge, outer[i], outer[i + 1], inner[i + 1], inner[i], 0.08);
    front = inner;
  }
  const panes = [];
  for (let i = 0; i < front.length - 1; i++) panes.push([front[i], front[i + 1], apex]);
  kit.tris(M.glass, panes);
  for (const q of front) kit.rod(M.metal, q, apex, 0.035);
  // Interior seen through the glass: floor, two-tier benches, the stove.
  kit.prism(M.interior, insetConvex(oct, 0.2), plinth, 0.08);
  kit.box(M.pine, xg + 0.2, plinth + 0.45, 0, 1.2, 0.5, 2 * a - 0.8);
  kit.box(M.pine, xg - 0.6, plinth + 0.9, 0, 0.9, 0.5, 2 * a - 0.8);
  const smoke = [];
  if (p.chimney && p.chimneyAt) {
    const [cx, cz] = p.chimneyAt, k = (H - wallR) / rw, kp = (H - wallH) / a;
    const roofY = Math.max(H - Math.abs(cz) * k, H - Math.hypot(cx - xg, cz) * kp);
    kit.cyl(M.metal, cx, roofY - 0.05, cz, 0.09, 0.09, 1.6, 8);
    smoke.push([cx, roofY + 0.85, cz]);
  }
  return { group: kit.build(), top: H + 0.4, smoke };
}
// ---- WS-10 terrace kit (end) --------------------------------------------------------------------
